/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AuthorizationCodesPage.tsx (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    List view for managing Authorization Codes (CRUD entry point).
    Provides COS-aware listing with CSV import/export actions.

 🔧 Notes:
    - Refactored from monolithic 425-line file
    - Logic delegated to hooks/
    - API calls moved to api/
    - JSX rendering for columns moved here from hook

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Edit, Trash2, Download, Upload, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModulePageContainer } from '../../components/layouts';
import { Badge } from '../../components/ui/badge';
import ImportAuthorizationCodesModal from './ImportAuthorizationCodesModal';
import { useAuthorizationCodeList } from './hooks';
import { deleteAuthorizationCodeById, navigateToAuthorizationCodeDuplicate } from './authorizationCodesActions';
import { exportAuthorizationCodes } from './api';
import type { AuthorizationCode } from './types';

export default function AuthorizationCodesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item: AuthorizationCode | null }>({
    isOpen: false,
    item: null
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
    tenantSlug
  } = useAuthorizationCodeList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.key === 'code') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => (
            <span className="font-medium text-foreground">{item.code}</span>
          )
        };
      }
      if (col.key === 'alias') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => (
            <span className="text-sm text-muted-foreground">{item.alias || item.code}</span>
          )
        };
      }
      if (col.key === 'description') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => (
            <span className="text-sm text-muted-foreground">{item.description || '—'}</span>
          )
        };
      }
      if (col.key === 'classOfService') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => (
            <span className="text-sm text-muted-foreground">{item.classOfService?.name ?? '—'}</span>
          )
        };
      }
      if (col.key === 'active') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => (
            <Badge variant={item.active ? 'default' : 'secondary'}>
              {item.active ? t('authorizationCodes.list.status.enabled') : t('authorizationCodes.list.status.disabled')}
            </Badge>
          )
        };
      }
      if (col.key === 'createdAt') {
        return {
          ...col,
          render: (_value: unknown, item: AuthorizationCode) => {
            const date = new Date(item.createdAt);
            return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
          }
        };
      }
      return col;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    if (!effectiveDomainId) {
      showError(t('authorizationCodes.messages.domainMissing'));
      return;
    }
    navigate('/pbx/class-of-service/auth-codes/new');
  }, [effectiveDomainId, navigate, showError, t]);

  const handleEdit = useCallback((item: AuthorizationCode) => {
    navigate(`/pbx/class-of-service/auth-codes/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((item: AuthorizationCode) => {
    setDeleteModal({ isOpen: true, item });
  }, []);

  const handleDuplicate = useCallback((item: AuthorizationCode) => {
    navigateToAuthorizationCodeDuplicate(navigate, item.uuid || item.id);
  }, [navigate]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.item) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAuthorizationCodeById(deleteModal.item.id, tenantSlug);

      showSuccess(t('authorizationCodes.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, item: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('authorizationCodes.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.item, refresh, showError, showSuccess, t, tenantSlug]);

  const renderActions = useMemo(() => (item: AuthorizationCode) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  const handleExport = useCallback(async () => {
    if (!effectiveDomainId) {
      showError(t('authorizationCodes.messages.domainMissing'));
      return;
    }

    setIsExporting(true);
    try {
      const blob = await exportAuthorizationCodes(effectiveDomainId, tenantSlug);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `authorization-codes-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess(t('authorizationCodes.messages.exportSuccess'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('authorizationCodes.messages.exportError');
      showError(message);
    } finally {
      setIsExporting(false);
    }
  }, [effectiveDomainId, showError, showSuccess, t, tenantSlug]);

  const handleOpenImport = useCallback(() => {
    if (!effectiveDomainId) {
      showError(t('authorizationCodes.messages.domainMissing'));
      return;
    }
    setImportModalOpen(true);
  }, [effectiveDomainId, showError, t]);

  const handleCloseImport = useCallback(() => {
    setImportModalOpen(false);
  }, []);

  const handleImportSuccess = useCallback(() => {
    refresh();
    setImportModalOpen(false);
    showSuccess(t('authorizationCodes.messages.importSuccess'));
  }, [refresh, showSuccess, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <div className="space-y-4">
          <DataGridSimple
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreate}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDeleteClick}
            onColumnToggle={handleColumnToggle}
            title={t('authorizationCodes.title')}
            titleIcon={<Shield className="h-5 w-5 text-primary" />}
            addButtonText={t('common.add')}
            renderActions={renderActions}
            actions={
              <div className="flex items-center gap-2">
                <SimpleTooltip content={t('authorizationCodes.actions.export')}>
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('authorizationCodes.actions.export')}</span>
                  </button>
                </SimpleTooltip>
                <SimpleTooltip content={t('authorizationCodes.actions.import')}>
                  <button
                    onClick={handleOpenImport}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('authorizationCodes.actions.import')}</span>
                  </button>
                </SimpleTooltip>
              </div>
            }
            emptyMessage={effectiveDomainId ? t('authorizationCodes.empty.description') : t('authorizationCodes.messages.domainMissing')}
          />
        </div>
      }
      formView={null}
    >
      <ImportAuthorizationCodesModal
        isOpen={importModalOpen}
        onClose={handleCloseImport}
        onSuccess={handleImportSuccess}
        tenantSlug={tenantSlug}
        domainId={effectiveDomainId}
      />
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={handleDeleteConfirm}
        title={t('authorizationCodes.messages.deleteConfirm')}
        message={t('authorizationCodes.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
